import {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {Analytics} from '../../../services/analytics';
import * as ExpectedPanelsActions from '../../../state/expectedPanels/actions';
import {useReduxAction, useReduxStore} from '../../../state/hooks';
import {RunQueryContext} from '../../../state/runs/context';
import {type useRunsWandbConfigQuery} from '../../../state/runs/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import {denormalize} from '../../../state/views/normalize';
import * as PanelBankConfigActionsInternal from '../../../state/views/panelBankConfig/actionsInternal';
import type * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import {type WholeFromTypeWithRef} from '../../../state/views/types';
import {getCache, getCacheStorageKey} from '../../../util/cache';
import {getExpectedPanelSpecs} from '../../../util/panelbank';
import {useCheckWeaveSignal} from '../../../util/useWeaveTrace';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {useDefinedMetricContext} from '../contextProviders/DefinedMetricContext';

export const useAutoSyncPanelBank = (
  panelBankConfigRef: PanelBankConfigTypes.Ref,
  wbConfigQuery: ReturnType<typeof useRunsWandbConfigQuery>
): {loading: boolean} => {
  const updateExpectedPanels = useReduxAction(ExpectedPanelsActions.update);
  const clearExpectedPanels = useReduxAction(ExpectedPanelsActions.clear);
  const diffAndInitPanels = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActionsInternal.diffAndInitPanels
  );

  // keep expected panels up to date
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const {
    workspaceLayout: {autoOrganizePrefix, shouldAutoGeneratePanels},
  } = useWorkspaceSettingsContext();
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const {definedMetricsMap, hiddenMetrics} = useDefinedMetricContext();
  const replacePanelBankConfig = useReduxAction(
    PanelBankConfigActionsInternal.replacePanelBankConfig
  );

  const [loading, setLoading] = useState<boolean>(true);

  const cache = getCache();

  const latestCacheKey = useRef<string | null>(null);

  const store = useReduxStore();

  const isFirstPBRender = useRef(true);

  // Heuristic: include Weave Trace panel only if project has trace data
  const shouldIncludeWeavePanel = useCheckWeaveSignal();

  const {entityName, projectName} = useContext(RunQueryContext);

  useEffect(() => {
    if (
      keyInfoQuery.loading ||
      keyInfoQuery.error ||
      wbConfigQuery.loading ||
      wbConfigQuery.error ||
      !wbConfigQuery.wandbConfig
    ) {
      return;
    }

    // We need this to not have any refs in the cache key
    // so that our cache key can be stable. Thats why we
    // use denormalize here. We also want to be very conservative
    // about calling denormalize because it's expensive. So we only
    // want to run it if the history keys or config have changed.
    const getCurrentPanelBankConfig = () => {
      const panelBankConfig = denormalize(
        store.getState().views.parts,
        panelBankConfigRef,
        {
          includeRefs: false,
        }
      );
      return panelBankConfig;
    };

    // must include `sortAlphabetically` workspace setting in cache key,
    // but we don't want to trigger panel gen whenever alpha sort setting
    // is touched. therefore, we have to get the setting value directly
    // from the store *inside* this effect hook
    const {sortAlphabetically} =
      store.getState().views.parts[workspaceSettingsRef.type][
        workspaceSettingsRef.id
      ] ?? {};

    const cacheKey = {
      keys: keyInfoQuery.historyKeyInfo.sortedKeys,
      viz: keyInfoQuery.viz,
      panelBankConfig: getCurrentPanelBankConfig(),
      autoOrganizePrefix,
      shouldAutoGeneratePanels,
      sortAlphabetically,
      definedMetricsMap,
      hiddenMetrics,
      shouldIncludeWeavePanel,
    };

    const populateExpectedPanels = () => {
      const expectedPanels = getExpectedPanelSpecs(
        keyInfoQuery.historyKeyInfo.keys,
        keyInfoQuery.viz,
        autoOrganizePrefix,
        definedMetricsMap,
        hiddenMetrics,
        shouldIncludeWeavePanel
      );
      updateExpectedPanels(expectedPanels);
      return expectedPanels;
    };

    const doDiffAndInitPanels = () => {
      const expectedPanels = populateExpectedPanels();
      if (shouldIncludeWeavePanel) {
        Analytics.track('Weave Trace Panel Is Included', {
          entityName,
          projectName,
        });
      }
      diffAndInitPanels(expectedPanels, workspaceSettingsRef);
      setLoading(false);
    };

    if (cache && isFirstPBRender.current) {
      const fileName = getCacheStorageKey('autoSyncPanelBank', cacheKey);
      latestCacheKey.current = fileName;

      (async () => {
        if (fileName) {
          const cachedData =
            await cache.get<WholeFromTypeWithRef<'panel-bank-config'>>(
              fileName
            );

          if (fileName !== latestCacheKey.current) {
            // stale request, ignore
            return;
          }

          if (cachedData) {
            replacePanelBankConfig(cachedData.data, panelBankConfigRef);
            setLoading(false);
            // still need to populate expected panels state after pb cache hit,
            // otherwise there won't be any expected panels for panel quick add
            populateExpectedPanels();
          } else {
            doDiffAndInitPanels();
            const panelBankConfig = getCurrentPanelBankConfig();
            cache.set<WholeFromTypeWithRef<'panel-bank-config'>>(
              fileName,
              panelBankConfig
            );
          }
        } else {
          doDiffAndInitPanels();
        }
      })();
    } else {
      doDiffAndInitPanels();
    }

    isFirstPBRender.current = false;

    return () => {
      clearExpectedPanels();
    };
  }, [
    keyInfoQuery,
    wbConfigQuery,
    definedMetricsMap,
    hiddenMetrics,
    autoOrganizePrefix,
    updateExpectedPanels,
    clearExpectedPanels,
    diffAndInitPanels,
    workspaceSettingsRef,
    setLoading,
    cache,
    panelBankConfigRef,
    replacePanelBankConfig,
    shouldAutoGeneratePanels,
    store,
    shouldIncludeWeavePanel,
    entityName,
    projectName,
  ]);
  return useMemo(() => ({loading}), [loading]);
};
